<template>
  <div
    class="grid grid-cols-2 gap-4 mb-4 md:grid-cols-2 md:gap-4 lg:grid-cols-4 lg:gap-6 lg:mb-8 w-full"
  >
    <!-- Total Students Card -->
    <div
      class="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 md:p-6 relative overflow-hidden"
    >
      <!-- Loader Overlay -->
      <div
        v-if="isLoading"
        class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center"
      >
        <div
          class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"
        ></div>
      </div>

      <!-- Main Content -->
      <div class="grid grid-cols-[1fr_auto] gap-3 sm:gap-4 items-start">
        <div>
          <p
            class="text-[9px] sm:text-xs md:text-sm font-semibold text-gray-500 uppercase tracking-wide"
          >
            Total Students
          </p>

          <p
            class="text-[22px] sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 mt-0.5 sm:mt-1 md:mt-2"
          >
            <template v-if="!isLoading">{{ students.length }}</template>
            <template v-else>--</template>
          </p>
          <!-- Status Info -->
          <div
            class="hidden sm:mt-3 sm:flex sm:flex-nowrap sm:items-center sm:gap-2 sm:overflow-x-auto sm:whitespace-nowrap text-[10px] sm:text-xs text-gray-600 mt-2"
          >
            <span
              v-if="activePct > 0"
              class="inline-flex items-center gap-1"
            >
              <span
                class="inline-block w-2 h-2 rounded-full bg-green-500"
              ></span>
              <span>Active {{ activePct }}%</span>
            </span>
            <span
              v-if="activePct > 0 && (inactivePct > 0 || graduatedPct > 0)"
              class="text-gray-300"
              >•</span
            >
            <span
              v-if="inactivePct > 0"
              class="inline-flex items-center gap-1"
            >
              <span class="inline-block w-2 h-2 rounded-full bg-red-500"></span>
              <span>Inactive {{ inactivePct }}%</span>
            </span>
            <span
              v-if="inactivePct > 0 && graduatedPct > 0"
              class="text-gray-300"
              >•</span
            >
            <span
              v-if="graduatedPct > 0"
              class="inline-flex items-center gap-1"
            >
              <span
                class="inline-block w-2 h-2 rounded-full bg-blue-500"
              ></span>
              <span class="text-[7px] sm:text-xs md:text-sm">
                Graduated {{ graduatedPct }}%
              </span>
            </span>
          </div>
        </div>
        <!-- Pie Chart -->
        <div>
          <svg
            class="w-16 h-16 sm:w-[68px] sm:h-[68px]"
            viewBox="0 0 48 48"
            role="img"
            aria-label="Students status distribution"
          >
            <circle
              cx="24"
              cy="24"
              :r="r"
              fill="transparent"
              stroke="#E5E7EB"
              :stroke-width="stroke"
            />
            <circle
              v-if="totalStatus > 0"
              cx="24"
              cy="24"
              :r="r"
              fill="transparent"
              stroke="#22C55E"
              :stroke-width="stroke"
              :stroke-dasharray="`${activeLen} ${circumference - activeLen}`"
              :stroke-dashoffset="0"
              transform="rotate(-90 24 24)"
              stroke-linecap="butt"
            />
            <circle
              v-if="totalStatus > 0 && inactiveLen > 0"
              cx="24"
              cy="24"
              :r="r"
              fill="transparent"
              stroke="#EF4444"
              :stroke-width="stroke"
              :stroke-dasharray="`${inactiveLen} ${
                circumference - inactiveLen
              }`"
              :stroke-dashoffset="`-${activeLen}`"
              transform="rotate(-90 24 24)"
              stroke-linecap="butt"
            />
            <circle
              v-if="totalStatus > 0 && graduatedLen > 0"
              cx="24"
              cy="24"
              :r="r"
              fill="transparent"
              stroke="#3B82F6"
              :stroke-width="stroke"
              :stroke-dasharray="`${graduatedLen} ${
                circumference - graduatedLen
              }`"
              :stroke-dashoffset="`-${activeLen + inactiveLen}`"
              transform="rotate(-90 24 24)"
              stroke-linecap="butt"
            />
            <text
              x="50%"
              y="50%"
              dominant-baseline="middle"
              text-anchor="middle"
              class="fill-gray-800"
              style="font-size: 8px; font-weight: 600"
            >
              {{ totalStatus }}
            </text>
          </svg>
        </div>
      </div>
    </div>

    <div v-if="activeStudents > 0">
      <div
        class="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 md:p-6 relative overflow-hidden"
      >
        <div
          v-if="isLoading"
          class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center"
        >
          <div
            class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 border-2 border-green-200 border-t-green-600 rounded-full animate-spin"
          ></div>
        </div>
        <div class="absolute top-2 right-2 sm:top-3 sm:right-3">
          <div class="p-1.5 bg-green-400/90 rounded-md border border-green-200">
            <UserCheck class="w-4 h-4 text-white opacity-95" />
          </div>
        </div>
        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0"
        >
          <div class="flex-1 min-w-0">
            <p
              class="text-[10px] sm:text-xs md:text-sm font-semibold text-gray-500 uppercase tracking-wide"
            >
              Active
            </p>
            <p
              class="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-green-400 mt-0.5 sm:mt-1 md:mt-2"
            >
              <template v-if="!isLoading">{{ activeStudents }}</template>
              <template v-else>--</template>
            </p>

            <div class="mt-2">
              <div class="h-2 rounded bg-gray-100 overflow-hidden">
                <div
                  class="h-2 bg-green-400 rounded"
                  :style="{ width: activePct + '%' }"
                  :aria-label="`Active ${activePct}% of total`"
                  role="progressbar"
                  :aria-valuenow="activePct"
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
              <p class="mt-1 text-[10px] sm:text-xs text-gray-500">
                {{ activePct }}% of total
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="graduatedStudents > 0">
      <div
        class="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 md:p-6 relative overflow-hidden"
      >
        <div
          v-if="isLoading"
          class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center"
        >
          <div
            class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 border-2 border-blue-200 border-t-blue-400 rounded-full animate-spin"
          ></div>
        </div>
        <div class="absolute top-2 right-2 sm:top-3 sm:right-3">
          <div class="p-1.5 bg-blue-400/90 rounded-md border border-blue-300">
            <GraduationCap class="w-4 h-4 text-white opacity-95" />
          </div>
        </div>
        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0"
        >
          <div class="flex-1 min-w-0">
            <p
              class="text-[10px] sm:text-xs md:text-sm font-semibold text-gray-500 uppercase tracking-wide"
            >
              Graduated
            </p>
            <p
              class="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-blue-400 mt-0.5 sm:mt-1 md:mt-2"
            >
              <template v-if="!isLoading">{{ graduatedStudents }}</template>
              <template v-else>--</template>
            </p>

            <!-- Progress Bar for Graduated -->
            <div class="mt-2">
              <div class="h-2 rounded bg-gray-100 overflow-hidden">
                <div
                  class="h-2 bg-blue-400 rounded"
                  :style="{ width: graduatedPct + '%' }"
                  :aria-label="`Graduated ${graduatedPct}% of total`"
                  role="progressbar"
                  :aria-valuenow="graduatedPct"
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
              <p class="mt-1 text-[10px] sm:text-xs text-gray-500">
                {{ graduatedPct }}% of total
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="inactiveStudents > 0">
      <div
        class="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 md:p-6 relative overflow-hidden"
      >
        <div
          v-if="isLoading"
          class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center"
        >
          <div
            class="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 border-2 border-red-200 border-t-red-400 rounded-full animate-spin"
          ></div>
        </div>
        <div class="absolute top-2 right-2 sm:top-3 sm:right-3">
          <div class="p-1.5 bg-red-400/90 rounded-md border border-red-200">
            <UserX class="w-4 h-4 text-white opacity-95" />
          </div>
        </div>
        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0"
        >
          <div class="flex-1 min-w-0">
            <p
              class="text-[10px] sm:text-xs md:text-sm font-semibold text-gray-500 uppercase tracking-wide"
            >
              Inactive
            </p>
            <p
              class="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-red-400 mt-0.5 sm:mt-1 md:mt-2"
            >
              <template v-if="!isLoading">{{ inactiveStudents }}</template>
              <template v-else>--</template>
            </p>

            <div class="mt-2">
              <div class="h-2 rounded bg-gray-100 overflow-hidden">
                <div
                  class="h-2 bg-red-400 rounded"
                  :style="{ width: inactivePct + '%' }"
                  :aria-label="`Inactive ${inactivePct}% of total`"
                  role="progressbar"
                  :aria-valuenow="inactivePct"
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
              <p class="mt-1 text-[10px] sm:text-xs text-gray-500">
                {{ inactivePct }}% of total
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed } from "vue";
  import { UserCheck, GraduationCap, UserX } from "lucide-vue-next";

  // capture props so we can compute chart values
  const props = defineProps({
    students: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    activeStudents: {
      type: Number,
      default: 0,
    },
    graduatedStudents: {
      type: Number,
      default: 0,
    },
    inactiveStudents: {
      type: Number,
      default: 0,
    },
  });

  // totals used for charts (status distribution only)
  const totalStatus = computed(() => {
    return (
      (props.activeStudents || 0) +
      (props.inactiveStudents || 0) +
      (props.graduatedStudents || 0)
    );
  });

  // percentages for “graph” bars
  const activePct = computed(() =>
    totalStatus.value
      ? Math.round((props.activeStudents / totalStatus.value) * 100)
      : 0
  );
  const inactivePct = computed(() =>
    totalStatus.value
      ? Math.round((props.inactiveStudents / totalStatus.value) * 100)
      : 0
  );
  const graduatedPct = computed(() =>
    totalStatus.value
      ? Math.round((props.graduatedStudents / totalStatus.value) * 100)
      : 0
  );

  // donut geometry
  const r = 18; // radius
  const stroke = 6; // ring thickness
  const circumference = computed(() => 2 * Math.PI * r);

  // segment lengths
  const activeLen = computed(() =>
    totalStatus.value
      ? circumference.value * (props.activeStudents / totalStatus.value)
      : 0
  );
  const inactiveLen = computed(() =>
    totalStatus.value
      ? circumference.value * (props.inactiveStudents / totalStatus.value)
      : 0
  );
  const graduatedLen = computed(() =>
    totalStatus.value
      ? circumference.value * (props.graduatedStudents / totalStatus.value)
      : 0
  );
</script>
